import React, { FC, ReactNode } from 'react';

import { Button, AssetWithIcon, Text, TooltipWrapper } from '@/shared/ui';
import { cn } from '@/shared/lib/tailwind-merge';
import { useInputBase } from '@/widgets/Modals';
import { formatSmart } from '@/shared/utils/helpers';
import { NumericInput } from '@/widgets/NumericInput';

export type SupplyInputBaseProps = {
    value: string;
    name: string;
    setValue: (value: string | null) => void;
    userBalance: string;
    tokenSymbol: string;
    disabled: boolean;
    decimals: number;
    maxBalance: string;
    label?: string | ReactNode;
    tooltipText?: string;
};

export const SupplyText = {
    MaxAvailable: 'Max Available',
    Tooltips: {
        MaxAvailable: 'The maximum amount you can supply to the pool.',
    },
};

export const SupplyInputBase: FC<SupplyInputBaseProps> = ({
    value,
    name,
    setValue,
    userBalance,
    tokenSymbol,
    disabled,
    decimals,
    maxBalance,
    label,
    tooltipText,
}) => {
    const { isError, handleAmountChange, handleMaxClick } = useInputBase({
        name,
        balance: userBalance,
        errorMessage: 'Insufficient balance',
        decimals,
    });

    return (
        <>
            <div
                className={cn(
                    'bg-background-secondary border border-border-opaque rounded-lg p-5 flex flex-col',
                    isError && 'border-content-negative',
                )}
            >
                <div className='flex justify-between items-center [&_input]:p-0 [&_input]:h-[28px]'>
                    <NumericInput
                        value={value}
                        onRawValueChange={async (rawValue) => {
                            await handleAmountChange(rawValue, setValue);
                        }}
                        decimals={decimals}
                        disabled={disabled}
                        name={name}
                    />
                    <div className='flex justify-end gap-2 min-w-[180px]'>
                        <AssetWithIcon
                            assetName={tokenSymbol}
                            withTooltip
                            className='text-xl font-light [&_img]:h-[28px] [&_img]:w-[28px [&_div]:max-w-[120px] [&_div]:text-ellipsis [&_div]:overflow-hidden [&_div]:whitespace-nowrap'
                        />
                    </div>
                </div>
            </div>

            <SupplyInputError isError={isError} />

            <div className='flex justify-between mt-3 text-sm'>
                <SupplyMaxAvailable
                    handleMaxClick={handleMaxClick}
                    maxBalance={maxBalance}
                    setValue={setValue}
                />

                <SupplyInputLabel label={label} tooltipText={tooltipText} />
            </div>
        </>
    );
};

export type SupplyInputErrorProps = {
    isError: { message: string } | null;
};

export const SupplyInputError: FC<SupplyInputErrorProps> = ({ isError }) => {
    return isError && <div className='text-content-negative text-sm mt-1'>{isError.message}</div>;
};

export type SupplyMaxAvailableProps = {
    maxBalance: string;
    handleMaxClick: (setValue: (value: string) => void, maxBalance: string) => void;
    setValue: (value: string) => void;
};

export const SupplyMaxAvailable: FC<SupplyMaxAvailableProps> = ({
    maxBalance,
    handleMaxClick,
    setValue,
}) => {
    return (
        <div className='flex items-center gap-1'>
            <Text type={Text.types.Paragraph} className='text-content-tertiary font-medium'>
                {SupplyText.MaxAvailable}
            </Text>
            <Button
                variant={Button.types.Link}
                type='button'
                name='max-available'
                onClick={() => handleMaxClick(setValue, maxBalance)}
                className='text-content-accent p-0 ml-2 rounded-none max-h-[20px]'
            >
                {formatSmart(maxBalance)}
            </Button>
        </div>
    );
};

export type SupplyInputLabelProps = {
    label: string | ReactNode;
    tooltipText?: string;
};

export const SupplyInputLabel: FC<SupplyInputLabelProps> = ({ label, tooltipText }) => {
    return (
        <div className='flex items-center gap-2 text-md'>
            {typeof label === 'string' ? (
                <Text type={Text.types.Paragraph} className='text-content-tertiary font-medium'>
                    {label}
                </Text>
            ) : (
                label
            )}
            {tooltipText && <TooltipWrapper text={tooltipText} />}
        </div>
    );
};
